import React from 'react'
import { Link } from 'react-router-dom'
import Button from '@/components/ui/Button'

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-b from-primary-50 to-background dark:from-primary-950/20 dark:to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground">
              Professional Trading Charts
              <span className="block text-primary-600">Made Simple</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Access comprehensive financial data and advanced technical indicators.
              Start trading smarter with GTCharts' professional-grade platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/charts">
                <Button size="lg" className="w-full sm:w-auto">
                  Try Charts Free
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  Create Account
                </Button>
              </Link>
            </div>
            <div className="text-sm text-muted-foreground">
              No credit card required • Completely free • Instant access
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Everything you need to trade
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional-grade tools and data that help traders make informed decisions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="card p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Real-time Data</h3>
              <p className="text-muted-foreground">
                Live market data from forex, stocks, crypto, and commodities with minimal latency
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Technical Indicators</h3>
              <p className="text-muted-foreground">
                50+ built-in indicators including RSI, MACD, Bollinger Bands, and custom overlays
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-foreground">Chart Analysis</h3>
              <p className="text-muted-foreground">
                Professional chart analysis tools with pattern recognition and trend analysis
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Access Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Try it now, no signup required
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience our professional trading platform with guest access. View real-time charts and basic indicators instantly.
            </p>

            {/* Sample chart preview would go here */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-background border border-border rounded-lg p-8 text-center">
                <div className="text-muted-foreground mb-4">
                  📈 Interactive Chart Preview
                </div>
                <div className="h-64 bg-muted rounded flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <div className="text-lg font-medium text-foreground">EUR/USD Chart</div>
                    <div className="text-sm text-muted-foreground">Real-time data • Guest access</div>
                    <Link to="/charts">
                      <Button>View Full Chart</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Access Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Free access to everything
            </h2>
            <p className="text-lg text-muted-foreground">
              No hidden fees, no premium tiers - just professional trading tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Guest Access */}
            <div className="card p-6 space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-foreground">Guest Access</h3>
                <div className="text-3xl font-bold text-foreground">Free</div>
                <p className="text-sm text-muted-foreground">No account needed</p>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Basic charts
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Real-time data
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  All timeframes
                </li>
              </ul>
              <Link to="/charts" className="w-full">
                <Button variant="secondary" className="w-full">
                  Try Now
                </Button>
              </Link>
            </div>

            {/* Registered Users */}
            <div className="card p-6 space-y-6 border-primary-200 dark:border-primary-800">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-foreground">Registered Users</h3>
                <div className="text-3xl font-bold text-foreground">Free</div>
                <p className="text-sm text-muted-foreground">Forever free</p>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  All guest features
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Technical indicators
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Save layouts
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  News access
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Personal dashboard
                </li>
              </ul>
              <Link to="/register" className="w-full">
                <Button className="w-full">
                  Create Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to start trading smarter?
          </h2>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto">
            Join traders using GTCharts professional tools - completely free, no strings attached
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                Create Free Account
              </Button>
            </Link>
            <Link to="/charts">
              <Button variant="ghost" size="lg" className="w-full sm:w-auto text-white border-white hover:bg-white/10">
                Try Guest Mode
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}